import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { generateAffiliateLink } from '../api/referral';

const Monetize = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [affiliateLink, setAffiliateLink] = useState('');
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [affiliateError, setAffiliateError] = useState('');
  const [referredUsers, setReferredUsers] = useState([]);
  const [referredLoading, setReferredLoading] = useState(false);
  const { user, fetchUserCommissions, getToken } = useAuth();

  useEffect(() => {
    if (user?.id) {
      fetchUserCommissions(user.id)
        .then((data) => {
          if (data && data.data && data.data.summary) {
            setStats(data.data.summary);
          } else {
            setError(data.message || 'Failed to fetch affiliate stats');
          }
        })
        .catch((err) => {
          setError('Failed to fetch affiliate stats');
        })
        .finally(() => {
          setLoading(false);
        });

      // Fetch referred users
      fetchReferredUsers();
    } else {
      setLoading(false);
    }
  }, [user, fetchUserCommissions]);

  const fetchReferredUsers = async () => {
    if (!user?.id) return;
    setReferredLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`/api/referral/referred/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setReferredUsers(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch referred users:', err);
    } finally {
      setReferredLoading(false);
    }
  };

  const handleGenerateAffiliateLink = async () => {
    if (!user?.id) {
      setAffiliateError('You must be registered to generate an affiliate link.');
      return;
    }
    setAffiliateLoading(true);
    setAffiliateError('');
    try {
      const response = await generateAffiliateLink(user.id);
      if (response.error === 'USER_NOT_FOUND') {
        setAffiliateError('You must register first before generating an affiliate link.');
      } else if (response.affiliateLink) {
        setAffiliateLink(response.affiliateLink);
      } else {
        setAffiliateError('Failed to generate affiliate link. Please try again.');
      }
    } catch (err) {
      setAffiliateError('An error occurred while generating the affiliate link.');
    } finally {
      setAffiliateLoading(false);
    }
  };

  const copyToClipboard = (link) => {
    if (link) {
      navigator.clipboard.writeText(link);
      alert('Affiliate link copied to clipboard!');
    }
  };

  const cardStyle = {
    border: '1px solid #181717ff',
    borderRadius: '8px',
    padding: '20px',
    margin: '10px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    backgroundColor: '#272626ff',
    color: '#fff',
    flex: '1',
    minWidth: '200px',
  };

  const containerStyle = {
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    minHeight: '100vh',
  };

  const gridStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
  };

  const sectionStyle = {
    marginBottom: '40px',
  };

  const buttonStyle = {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '10px',
  };

  const inputStyle = {
    width: '100%',
    padding: '8px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#333',
    color: '#fff',
  };

  if (loading) {
    return <div style={containerStyle}>Loading...</div>;
  }

  if (error) {
    return <div style={containerStyle}>{error}</div>;
  }

  const commissionRate = (stats?.totalCommissions || 0) > 3 ? 0.3 : 0.2;

  return (
    <div style={containerStyle}>
      <h1>Professional Affiliate Program</h1>
      <p>Earn commissions by referring new users to RapidMind. Share your affiliate link and get paid when they subscribe!</p>

      {/* Affiliate Link Section */}
      <div style={sectionStyle}>
        <h2>Your Affiliate Link</h2>
        <p>Generate your unique affiliate link below. Only registered users can create affiliate links.</p>
        <button
          onClick={handleGenerateAffiliateLink}
          disabled={affiliateLoading}
          style={{ ...buttonStyle, opacity: affiliateLoading ? 0.6 : 1 }}
        >
          {affiliateLoading ? 'Generating...' : 'Generate Affiliate Link'}
        </button>
        {affiliateError && <p style={{ color: 'red' }}>{affiliateError}</p>}
        {affiliateLink && (
          <div style={{ marginTop: '20px' }}>
            <input type="text" value={affiliateLink} readOnly style={inputStyle} />
            <button onClick={() => copyToClipboard(affiliateLink)} style={{ ...buttonStyle, marginLeft: '10px' }}>
              Copy Link
            </button>
          </div>
        )}
      </div>

      {/* Stats Section */}
      <div style={sectionStyle}>
        <h2>Your Earnings Overview</h2>
        <div style={gridStyle}>
          <div style={cardStyle}>
            <h3>Total Earned</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold' }}>${stats ? stats.totalEarned.toFixed(2) : '0.00'}</p>
          </div>
          <div style={cardStyle}>
            <h3>Total Pending</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold' }}>${stats ? stats.totalPending.toFixed(2) : '0.00'}</p>
          </div>
          <div style={cardStyle}>
            <h3>Total Paid</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold' }}>${stats ? stats.totalPaid.toFixed(2) : '0.00'}</p>
          </div>
          <div style={cardStyle}>
            <h3>Total Referrals</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats ? stats.totalCommissions : '0'}</p>
          </div>
        </div>
        <p style={{ marginTop: '20px' }}>
          Current Commission Rate: <strong>{(commissionRate * 100).toFixed(0)}%</strong>
          {(stats?.totalCommissions || 0) > 3 ? ' (Bonus rate for 3+ successful referrals!)' : ' (Earn 30% once you have 3+ successful referrals)'}
        </p>
      </div>

      {/* Referred Users Section */}
      <div style={sectionStyle}>
        <h2>Your Referred Users</h2>
        {referredLoading ? (
          <p>Loading referred users...</p>
        ) : referredUsers.length > 0 ? (
          <div style={gridStyle}>
            {referredUsers.map((ref, index) => (
              <div key={index} style={cardStyle}>
                <h4>User ID: {ref.referredId}</h4>
                <p>Plan: {ref.planChosen || 'N/A'}</p>
                <p>Commission: ${ref.commission?.toFixed(2) || '0.00'}</p>
                <p>Status: {ref.status || 'Pending'}</p>
              </div>
            ))}
          </div>
        ) : (
          <p>No referred users yet. Share your affiliate link to start earning!</p>
        )}
      </div>

      {/* Guide Section */}
      <div style={sectionStyle}>
        <h2>How the Affiliate Program Works</h2>
        <div style={cardStyle}>
          <h3>Step 1: Get Your Affiliate Link</h3>
          <p>Click "Generate Affiliate Link" above to get your unique referral URL.</p>
        </div>
        <div style={cardStyle}>
          <h3>Step 2: Share Your Link</h3>
          <p>Share your affiliate link on social media, blogs, or with friends. When someone clicks and signs up, you're credited!</p>
        </div>
        <div style={cardStyle}>
          <h3>Step 3: Earn Commissions</h3>
          <p>Earn 20% commission on each subscription. Boost to 30% once you have 3+ successful referrals!</p>
        </div>
        <div style={cardStyle}>
          <h3>Step 4: Get Paid</h3>
          <p>Once you reach $10 in earnings, request a payout. Commissions are paid monthly.</p>
        </div>
        <div style={cardStyle}>
          <h3>Tips for Success</h3>
          <ul>
            <li>Share on platforms where your audience is active</li>
            <li>Create content explaining why RapidMind is great</li>
            <li>Track your referrals and optimize your sharing strategy</li>
            <li>Engage with your network to build trust</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Monetize;
